import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, model, output } from '@angular/core';
import {
  GeoJSONSourceComponent,
  LayerComponent,
  MapComponent,
  MarkerComponent,
  PopupComponent,
} from '@maplibre/ngx-maplibre-gl';
import { FastSvgComponent } from '@push-based/ngx-fast-svg';
import { Dialog } from 'primeng/dialog';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';

/**
 * Optional dashed-line overlay: each entry draws a line from the gateway position to
 * one of its antennas, with an "RF OUT N" label at the midpoint. Mirrors the wiring
 * overlay used on the site-configuration preview so the dialog shows exactly the same
 * information the integrator will dictate over the phone. Empty by default — the
 * gateway-detail page populates it with one entry per antenna.
 */
export interface GatewayAntennaLine {
  gatewayLng: number;
  gatewayLat: number;
  antennaLng: number;
  antennaLat: number;
  /** Resolved RF OUT pin (rfOut ?? orderGateway). Drives the midpoint text label. */
  rfOut: number;
  /** Used to key the underlying geojson sources. */
  key: string;
}

/**
 * One marker to draw on the dialog map. {@code kind} drives the icon + colour:
 * <ul>
 *   <li>{@code gateway-ref} — large yellow {@code gateway-ref} SVG (matches the inside admin
 *       reference-gateway visual).</li>
 *   <li>{@code gateway} — smaller yellow {@code gateway} SVG (any gateway that's not order=0
 *       or when there's only one).</li>
 *   <li>{@code antenna} — smaller purple {@code gateway} SVG with the order in the popup.</li>
 * </ul>
 *
 * <p>Popups follow the {@code [marker]="markerRef"} pattern so they only open when the user
 * clicks the marker — using {@code [lngLat]} would force every popup permanently open and
 * clutter the map (the previous version of this component had that bug).
 */
export interface LocationMapMarker {
  longitude: number;
  latitude: number;
  label: string;
  kind: 'gateway' | 'gateway-ref' | 'antenna';
  /** Order-on-site for antennas / gateways; surfaced in the popup label. */
  order?: number;
  /** Optional sub-label (deviceId, alt, …) shown in the popup. */
  sublabel?: string;
  /** Optional explicit colour override; otherwise we pick from the kind palette. */
  color?: string;
}

@Component({
  selector: 'lib-location-map-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    Dialog,
    MapComponent,
    MarkerComponent,
    PopupComponent,
    GeoJSONSourceComponent,
    LayerComponent,
    FastSvgComponent,
    TranslocoModule,
  ],
  template: `
    <p-dialog
      [(visible)]="visible"
      [header]="header()"
      [modal]="true"
      [style]="{ width: '80vw', maxWidth: '1100px' }"
      [contentStyle]="{ padding: 0 }"
      (onHide)="closed.emit()">
      <div class="h-[70vh] w-full overflow-hidden">
        @if (markers().length === 0) {
          <div class="flex h-full items-center justify-center text-sm text-gray-500">
            {{ 'locationMapDialog.noPosition' | transloco }}
          </div>
        } @else {
          <mgl-map
            [mapStyle]="mapStyle()"
            [zoom]="[mapZoom()]"
            [center]="mapCenter()"
            [maxZoom]="20"
            class="h-full w-full">
            <!-- Dashed wiring lines: gateway → each antenna with an "RF OUT N" label
                 at the midpoint. Same overlay pattern as the site-configuration preview,
                 so the integrator sees the exact same picture the field tech needs. -->
            @for (line of gatewayAntennaLines(); track line.key) {
              <mgl-geojson-source [id]="'gw-ant-line-' + line.key" [data]="buildLineFeature(line)"></mgl-geojson-source>
              <mgl-layer
                [id]="'gw-ant-line-layer-' + line.key"
                [source]="'gw-ant-line-' + line.key"
                type="line"
                [paint]="{ 'line-color': '#6B7280', 'line-width': 1.5, 'line-dasharray': [4, 3] }"></mgl-layer>
              <mgl-geojson-source
                [id]="'gw-ant-label-' + line.key"
                [data]="buildLabelFeature(line)"></mgl-geojson-source>
              <mgl-layer
                [id]="'gw-ant-label-layer-' + line.key"
                [source]="'gw-ant-label-' + line.key"
                type="symbol"
                [layout]="{
                  'text-field': ['get', 'label'],
                  'text-size': 10,
                  'text-anchor': 'center',
                  'text-offset': [0, -0.8],
                }"
                [paint]="{
                  'text-color': '#374151',
                  'text-halo-color': '#ffffff',
                  'text-halo-width': 1,
                }"></mgl-layer>
            }

            @for (m of markers(); track m.label + '-' + m.longitude) {
              <mgl-marker #markerRef [lngLat]="[m.longitude, m.latitude]">
                <div class="relative flex flex-col items-center">
                  @switch (m.kind) {
                    @case ('gateway-ref') {
                      <!-- Reference gateway — isometric chassis SVG, wider rectangular
                           frame so the box aspect (430×235) isn't squished. Strokes pick
                           up markerColor() via currentColor; no bg so the icon stays
                           transparent on the map. -->
                      <button class="flex h-9 w-16 items-center justify-center" [style.color]="markerColor(m)">
                        <fast-svg name="gateway-iso" size="100%"></fast-svg>
                      </button>
                    }
                    @case ('gateway') {
                      <!-- Non-reference gateway — same iso SVG, slightly smaller frame. -->
                      <button class="flex h-7 w-12 items-center justify-center" [style.color]="markerColor(m)">
                        <fast-svg name="gateway-iso" size="100%"></fast-svg>
                      </button>
                    }
                    @case ('antenna') {
                      <!-- Antenna — keeps the original 'gateway' icon (small radiating
                           element, distinct from the chassis box). -->
                      <button class="relative flex size-6" [style.color]="markerColor(m)">
                        <fast-svg name="gateway" size="100%"></fast-svg>
                      </button>
                    }
                  }
                </div>
              </mgl-marker>

              <mgl-popup [offset]="16" [marker]="markerRef" [closeButton]="false" [maxWidth]="'250'">
                <h4 class="text-base font-semibold" [style.color]="markerColor(m)">
                  {{ m.label }}
                  @if (m.kind === 'antenna' && m.order !== null && m.order !== undefined) {
                    <span class="text-gray-500">{{
                      'locationMapDialog.popup.order' | transloco: { order: m.order }
                    }}</span>
                  } @else if (m.order !== null && m.order !== undefined) {
                    <span class="text-gray-500">{{
                      'locationMapDialog.popup.order' | transloco: { order: m.order }
                    }}</span>
                  }
                </h4>
                @if (m.sublabel) {
                  <div class="text-xs text-gray-600">{{ m.sublabel }}</div>
                }
                <div class="mt-1 flex items-center gap-2 text-xs text-gray-700">
                  <i class="pi pi-map-marker" [style.color]="markerColor(m)"></i>
                  <span>
                    <span class="font-bold">{{ 'locationMapDialog.popup.lat' | transloco }}</span>
                    {{ m.latitude.toFixed(6) }}
                    <span class="font-bold">{{ 'locationMapDialog.popup.lng' | transloco }}</span>
                    {{ m.longitude.toFixed(6) }}
                  </span>
                </div>
              </mgl-popup>
            }
          </mgl-map>
        }
      </div>
    </p-dialog>
  `,
})
export class LocationMapDialogComponent {
  /** Two-way bindable open state. */
  visible = model.required<boolean>();

  #transloco = inject(TranslocoService);

  /** Modal header — typically the device or site name. Defaults to a translated "Location". */
  header = input<string>(this.#transloco.translate('locationMapDialog.defaultHeader'));

  /** Markers to render. Pass an empty array to show the "no position" placeholder. */
  markers = input.required<LocationMapMarker[]>();

  /** MapTiler API key (the integrator usually injects it via DI). When empty, falls back
   * to the demo tiles which are fine for sparse static-marker use cases. */
  maptilesKey = input<string>('');

  /** Emitted after the dialog closes (Esc, X, backdrop click). The integrator can use this
   * to free a parent signal that drove visibility. */
  closed = output<void>();

  protected readonly mapStyle = computed(() => {
    const key = this.maptilesKey();
    if (key) {
      return `https://api.maptiler.com/maps/streets-v2/style.json?key=${key}`;
    }
    return 'https://demotiles.maplibre.org/style.json';
  });

  /** Centre on the first marker; mapZoom adapts to spread (1 marker → 18, several → 17). */
  protected readonly mapCenter = computed<[number, number] | undefined>(() => {
    const m = this.markers();
    if (m.length === 0) return undefined;
    return [m[0].longitude, m[0].latitude];
  });

  protected readonly mapZoom = computed(() => (this.markers().length > 1 ? 17 : 18));

  /**
   * Dashed lines from gateway to antenna with the RF OUT label. Empty by default;
   * the gateway-detail page populates this when the rig has antennas so the dialog
   * shows the same wiring overlay the configuration preview does.
   */
  gatewayAntennaLines = input<GatewayAntennaLine[]>([]);

  /** Default palette per kind; explicit `color` on the marker overrides.
   *  Gateway = chassis box → black so the iso strokes read clearly against the map.
   *  Antenna = yellow → distinct from the gateway and visible on satellite tiles. */
  protected markerColor(m: LocationMapMarker): string {
    if (m.color) return m.color;
    switch (m.kind) {
      case 'gateway':
      case 'gateway-ref':
        return '#000000';
      case 'antenna':
        return '#ffc800';
    }
  }

  /** Single LineString feature between gateway origin and antenna position. */
  protected buildLineFeature(l: GatewayAntennaLine): GeoJSON.FeatureCollection {
    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: [
              [l.gatewayLng, l.gatewayLat],
              [l.antennaLng, l.antennaLat],
            ],
          },
          properties: {},
        },
      ],
    };
  }

  /** Single Point feature at the midpoint carrying the "RF OUT N" label string. */
  protected buildLabelFeature(l: GatewayAntennaLine): GeoJSON.FeatureCollection {
    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [(l.gatewayLng + l.antennaLng) / 2, (l.gatewayLat + l.antennaLat) / 2],
          },
          properties: { label: `RF OUT ${l.rfOut}` },
        },
      ],
    };
  }
}
